import React from 'react';
import { Mono } from '@jotpsych/ui';
import { Box, Stack, Tooltip } from '@mui/material';

import { CodeChip } from './CodeChip';
import { codeFontSize } from './codeFontSize';

/** Every code, one per line, in the claim's own order. */
const CodeList: React.FC<{ codes: readonly string[] }> = ({ codes }) => (
  <Mono component="div" sx={{ fontSize: codeFontSize }}>
    {codes.map((code, index) => (
      <Box key={`${index}-${code}`}>{code}</Box>
    ))}
  </Mono>
);

/**
 * A claim's procedure codes beside its charge: the first as a chip, the rest
 * folded into a "+N" chip, and the whole list on hover. Nothing at all when
 * the claim carries no code, so the cell keeps the charge alone.
 */
export const ProcedureCodeChips: React.FC<{ codes: readonly string[] }> = ({ codes }) => {
  const [first, ...rest] = codes;
  if (!first) return null;
  if (rest.length === 0) return <CodeChip>{first}</CodeChip>;
  /* The fold hides codes, so the group is a tab stop with the full list as
     its name: the tooltip opens on keyboard focus as it does on hover, and a
     reader that never focuses it still hears every code. A lone chip hides
     nothing and stays out of the tab order. */
  return (
    <Tooltip title={<CodeList codes={codes} />}>
      <Stack
        aria-label={`Procedure codes: ${codes.join(', ')}`}
        component="span"
        direction="row"
        role="group"
        spacing={0.5}
        sx={{
          display: 'inline-flex',
          flexShrink: 0,
          '&:focus-visible': {
            outline: '2px solid',
            outlineColor: 'primary.main',
            outlineOffset: 2,
          },
        }}
        tabIndex={0}
      >
        <CodeChip>{first}</CodeChip>
        <CodeChip>{`+${rest.length}`}</CodeChip>
      </Stack>
    </Tooltip>
  );
};
